import { motion } from "framer-motion";
import { ShieldCheck, Star, Globe } from "lucide-react";

interface PillarCardsProps {
  t: (key: string) => string;
}

const PILLAR_ICONS = [ShieldCheck, Star, Globe];

export function PillarCards({ t }: PillarCardsProps) {
  const pillars = [
    { title: t("home.pillar1Title"), text: t("home.pillar1Text") },
    { title: t("home.pillar2Title"), text: t("home.pillar2Text") },
    { title: t("home.pillar3Title"), text: t("home.pillar3Text") },
  ];

  return (
    <section className="py-24 border-y border-border/20 bg-card/20" data-testid="section-pillars">
      <div className="max-w-6xl mx-auto px-6">
        <motion.h2
          className="text-2xl md:text-3xl font-serif font-bold text-center text-foreground mb-4 max-w-2xl mx-auto"
          style={{ fontFamily: "'Playfair Display', serif" }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          data-testid="text-pillars-title"
        >
          {t("home.pillarsTitle")}
        </motion.h2>

        <motion.div
          className="w-12 h-0.5 bg-primary/40 mx-auto mb-14"
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {pillars.map((pillar, i) => {
            const Icon = PILLAR_ICONS[i];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`card-pillar-${i}`}
                className="group"
              >
                <div className="h-full rounded-xl border border-primary/30 bg-background/95 backdrop-blur-sm px-7 py-8 transition-all duration-300 hover:border-primary/60 hover:bg-primary/[0.06] hover:shadow-md hover:shadow-primary/5">
                  <div className="w-11 h-11 rounded-lg border border-primary/30 bg-primary/5 flex items-center justify-center mb-5 transition-colors duration-300 group-hover:border-primary/40 group-hover:bg-primary/10">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3
                    className="text-base font-semibold text-foreground mb-3"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {pillar.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{pillar.text}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
